import { Component, AfterViewInit, ViewChild, ElementRef, OnDestroy, Inject, Optional } from "@angular/core";
import { CodeEditor } from "../code-editor-core/editor";
import { TSEditor } from "../code-editor-core/ts/editor";
import { JavaEditor } from "../code-editor-core/java/editor";
import { HighLightEditor } from "../code-editor-core/other/editor";
import { IClass, IMethod, ChangeInfo } from "../code-editor-core/libs/interfaces/declaration";
import { Events } from "../code-editor-core/libs/enum";
import { tsHooks, highLightHooks, loadMonaco } from "./hooks/hooks";
import { addResizeListener, removeResizeListener } from '../utils/resize-event';
import { MonacoCodeEditorConfig, MONACO_CODE_EDITOR_CONFIG } from "./config";
import { IEditorOptions, IGlobalEditorOptions, IStandaloneThemeData } from './monaco.interface';
import { TsFileLoaderService } from './hooks/ts-file-loader.service';
import { TsPackageLoaderService } from './hooks/ts-package-loader.service';

/** 加载并初始化monaco编辑器 */
let loadPromise: Promise<void>;

type DefineThemeFn = (themeName: string, themeData: IStandaloneThemeData) => void;

@Component({
  selector: 'monaco-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss']
})
export class MonacoCodeEditorComponent implements AfterViewInit, OnDestroy {

  constructor(
    private tsFileLoader: TsFileLoaderService,
    private tsPackageLoader: TsPackageLoaderService,
    @Optional() @Inject(MONACO_CODE_EDITOR_CONFIG) private config?: MonacoCodeEditorConfig
  ) {}

  private tsEditor: TSEditor;

  private javaEditor: JavaEditor;

  private otherEditor: HighLightEditor;

  @ViewChild("editor") editorViewChild: ElementRef;

  @ViewChild("wrap") wrapViewChild: ElementRef;

  ngAfterViewInit(): void {
    if (!loadPromise) {
      loadPromise = new Promise<void>((resolve) => {
        const baseUrl = this.config && this.config.baseUrl || 'assets/monaco-editor';
        const language = this.getLanguage()
        const onGotAmdLoader: any = () => {
          (<any>window).require.config({ 
            paths: { 'vs': `${baseUrl}/vs` }, 
            'vs/nls': { 'availableLanguages': { '*': language } } 
          });
          this.init();
          resolve();
        };
        // load AMD loader if necessary
        if ( !(<any>window).require ) {
          const loaderScript: HTMLScriptElement = document.createElement('script');
          loaderScript.type = 'text/javascript';
          loaderScript.src = `${baseUrl}/vs/loader.js`;
          loaderScript.addEventListener('load', onGotAmdLoader);
          document.body.appendChild(loaderScript);
        } else {
          onGotAmdLoader();
        }
      });
    }
    this.defineCustomTheme();
  }

  /** 在默认主题的基础上自定义样式 */
  private async defineCustomTheme(): Promise<void> {
    await loadPromise;
    const monaco: any = await loadMonaco();
    const defineTheme: DefineThemeFn = monaco && monaco.editor && monaco.editor.defineTheme;
    if (!defineTheme || typeof defineTheme !== 'function') {
      return;
    }
    defineTheme("vs", {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#FFFFFF'
      }
    });
  }

  ngOnDestroy(): void {
    removeResizeListener(this.wrapViewChild.nativeElement, this.handleContainerResize);
  }

  private init(): void {
    // 设置编辑器钩子函数
    tsHooks.loadTSFiles = this.tsFileLoader.load.bind(this.tsFileLoader);
    tsHooks.loadTSPackages = this.tsPackageLoader.load.bind(this.tsPackageLoader);
    tsHooks.getDtsManifest = this.tsPackageLoader.getDtsManifest.bind(this.tsPackageLoader);
    // 实例化各个语言的编辑器服务
    this.tsEditor = new TSEditor(this.editorViewChild.nativeElement, tsHooks);
    this.javaEditor = new JavaEditor(this.editorViewChild.nativeElement, highLightHooks);
    this.otherEditor = new HighLightEditor(this.editorViewChild.nativeElement, highLightHooks);
    // 监听处理窗口大小变化事件
    addResizeListener(this.wrapViewChild.nativeElement, this.handleContainerResize);
  }

  /**
   * 根据文件后缀判断使用哪个编辑器打开
   * @param path 文件路径
   * @returns 代码编辑器
   */
  private openWith(path: string): CodeEditor {
    if (!path) {
      return null;
    }
    // 对于ts和java文件，采用专门的编辑器进行编辑
    if ( path.toLowerCase().endsWith(".ts") ) {
      return this.tsEditor;
    }
    else if ( path.toLowerCase().endsWith(".java") ) {
      return this.javaEditor;
    }
    else {
      // 对于其它类型的文件，一律采用高亮编辑器进行编辑
      return this.otherEditor;
    }
  }

  /**
   * 打开文件
   * @param path - 文件路径
   * @param content - 文件内容
   */
  public async open(path: string, content?: string): Promise<void> {
    await loadPromise;
    const editor = this.openWith(path);
    if (!editor) {
      return;
    }
    await editor.open(path, content);
  }

  /**
   * 获取文件解析结果
   * @param path - 文件路径
   * @param errorRecovery - 是否忽略语法错误并继续解析
   * @returns 文件内容及类结构，如果文件尚未打开则返回null
   */
  public async resolve(path: string, errorRecovery: boolean = false): Promise<{ content: string; hasFatalError?: boolean; classes?: IClass[]; }> {
    await loadPromise;
    const editor = this.openWith(path);
    if (!editor) {
      return null;
    }
    return await editor.resolve(path, errorRecovery);
  }

  /**
   * 保存文件
   * @remarks
   * 将文件的changed属性设置为false，并且记录最后一次保存的文件内容，允许撤销修改
   * 如果文件尚未打开则静默失败
   * @param path - 文件路径
   * @param triggerChangedEvent - 是否触发内容变化事件（默认不触发，如果设置为true，则将触发Changed和OutlineChanged事件，当然，changed属性一定为false）
   */
  public async save(path: string, triggerChangedEvent: boolean = false): Promise<void> {
    await loadPromise;
    const editor = this.openWith(path);
    if (!editor) {
      return;
    }
    await editor.save(path, triggerChangedEvent);
  }

  /**
   * 类新增方法片段
   * @param path 文件路径
   * @param method 方法结构描述
   * @param _class 类名，为空则默认第一个类
   */
  public async addMethod(path: string, method: IMethod, _class?: string): Promise<void> {
    await loadPromise;
    const editor = this.openWith(path);
    if (!editor) {
      return;
    }
    await editor.addMethod(path, method, _class);
  }

  /**
   * 关闭文件
   * @param path - 文件路径
   */
  public close(path: string): void {
    // 当编辑器找不到对应的文件时会静默失败
    this.tsEditor && this.tsEditor.close(path, true);
    this.javaEditor && this.javaEditor.close(path, true);
    this.otherEditor && this.otherEditor.close(path, true);
  }

  /** 处理容器大小变化事件 */
  private handleContainerResize = () => {
    this.tsEditor && this.tsEditor.layout();
    this.javaEditor && this.javaEditor.layout();
    this.otherEditor && this.otherEditor.layout();
  };

  /**
   * 监听内容变化事件
   * @remarks 注意：本方法的changeInfo中不会包含类结构信息，需要类结构信息需要订阅onOutlineChanged方法
   * @param fn 内容变化回调方法
   */
  public async onChanged(fn: (path: string, changeInfo: ChangeInfo) => void): Promise<void> {
    await loadPromise;
    this.tsEditor && this.tsEditor.on(Events.Changed, fn);
    this.javaEditor && this.javaEditor.on(Events.Changed, fn);
    this.otherEditor && this.otherEditor.on(Events.Changed, fn);
  }

  /**
   * 监听内容变化事件（该方法是经过防抖处理的）
   * @param fn 内容变化回调方法
   */
  public async onOutlineChanged(fn: (path: string, changeInfo: ChangeInfo) => void): Promise<void> {
    await loadPromise;
    this.tsEditor && this.tsEditor.on(Events.OutlineChanged, fn);
    this.javaEditor && this.javaEditor.on(Events.OutlineChanged, fn);
    this.otherEditor && this.otherEditor.on(Events.OutlineChanged, fn);
  }

  /**
   * 更改编辑器配置
   * @param path 文件路径
   * @param option 配置对象
   */
  public async updateOptions(path: string, newOptions: IEditorOptions & IGlobalEditorOptions): Promise<void> {
    await loadPromise;
    const editor = this.openWith(path);
    if (!editor || !newOptions) {
      return;
    }
    const file = editor.getExistFile(path);
    file && file.instance && file.instance.updateOptions && file.instance.updateOptions(newOptions);
  }

  /**
   * 设置主题
   * @param isDarkTheme 是否深色主题
   */
  public async setTheme(isDarkTheme: boolean): Promise<void> {
    await loadPromise;
    this.tsEditor && this.tsEditor.setDark(isDarkTheme);
    this.javaEditor && this.javaEditor.setDark(isDarkTheme);
    this.otherEditor && this.otherEditor.setDark(isDarkTheme);
  }

  /**
   * 定位当前光标位置
   * @param path 文件路径
   * @param className 类名
   * @param methodName 方法名
   */
  public async positon(path: string, className: string, methodName: string): Promise<void> {
    await loadPromise;
    const editor = this.openWith(path);
    if (!editor) {
      return;
    }
    editor.position(path, className, methodName);
  }


  //获取monaco-editor所使用的语言
  getLanguage() {
    let languageCode = 'zh-CHS';
    if (window && window.top && window.top.localStorage && window.top.localStorage.languageCode) {
      languageCode = window.top.localStorage.languageCode;
    }
    switch (languageCode) {
      case ('en'):
        return '';
        break;
      case ('zh-CHS'):
        return 'zh-cn';
        break;
      case ('zh-CHT'):
        return 'zh-tw';
        break;
      default:
        return 'zh-cn';
    }

  }
}
